/**
 * Page: CreateWebsite
 * Admin-only page to create a new website. Handles auth gate,
 * submission to the API, and basic UX states.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ArrowLeft, Upload, Loader2 } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import ThumbnailUploader from '../../../components/admin/stockVideos/ThumbnailUploader';
import Footer from '../../../components/shared/Footer';
import { createWebsite, getWebsiteUploadUrl } from '../../../api/websites/websitesApi';
import { WEBSITE_TAG_LIST } from '../../../data/tags/tagEnums';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function CreateWebsite() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [videoFile, setVideoFile] = useState(null);

  const [formData, setFormData] = useState({
    tags: [],
    websiteUrl: '',
    searchableText: '',
    videoUrl: '',
    thumbnailImage: null
  });

  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagChange = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleThumbnailChange = (thumbnailData) => {
    if (thumbnailData === null) {
      setFormData(prev => ({ ...prev, thumbnailImage: null }));
    } else if (thumbnailData instanceof File) {
      setFormData(prev => ({ ...prev, thumbnailImage: thumbnailData }));
    } else if (thumbnailData?.isExistingThumbnail) {
      setFormData(prev => ({ ...prev, thumbnailImage: thumbnailData }));
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setVideoFile(file);
    setIsUploadingVideo(true);
    setUploadProgress(0);

    try {
      const { uploadUrl, permanentUploadPath } = await getWebsiteUploadUrl();

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          setFormData(prev => ({ ...prev, videoUrl: permanentUploadPath }));
          addToast('Video uploaded successfully!', 'success');
        } else {
          throw new Error('Upload failed');
        }
        setIsUploadingVideo(false);
      });

      xhr.addEventListener('error', () => {
        addToast('Failed to upload video', 'error');
        setIsUploadingVideo(false);
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);

    } catch (error) {
      console.error('Error uploading video:', error);
      addToast('Failed to upload video. Please try again.', 'error');
      setIsUploadingVideo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.websiteUrl || !formData.videoUrl || formData.tags.length === 0) {
      addToast('Please fill in all required fields', 'error');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const submitData = new FormData();
      submitData.append('websiteUrl', formData.websiteUrl);
      submitData.append('videoUrl', formData.videoUrl);
      submitData.append('tags', formData.tags.join(','));
      submitData.append('searchableText', formData.searchableText || '');
      
      if (formData.thumbnailImage) {
        submitData.append('thumbnailImage', formData.thumbnailImage);
      }

      const response = await createWebsite(submitData);

      if (response && response.websiteId) {
        addToast('Website created successfully!', 'success');
        navigate('/app/private/manageWebsites');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error creating website:', error);
      
      let errorMessage = 'Failed to create website. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading create website..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/templates')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Templates
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create Website</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload and create a new website for the library.
          </p>
        </div>
        <button
          onClick={() => navigate('/app/private/manageWebsites')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          Manage Websites
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
        {submitError && (
          <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Creating Website</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Website URL */}
          <div>
            <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Website URL *
            </label>
            <input
              type="url"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              required
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tags *
            </label>
            <div className="flex flex-wrap gap-2">
              {WEBSITE_TAG_LIST.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagChange(tag)}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    formData.tags.includes(tag)
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Searchable Text */}
          <div>
            <label htmlFor="searchableText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Terms (Optional)
            </label>
            <textarea
              id="searchableText"
              name="searchableText"
              value={formData.searchableText}
              onChange={handleInputChange}
              rows={3}
              placeholder="Add comma-separated search terms to help users find this website..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Add search terms that describe the website content, style, or use cases. Tags are automatically included.
            </p>
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Preview Video *
            </label>
            {formData.videoUrl ? (
              <div className="flex items-center gap-2 p-3 border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-md">
                <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-green-800 dark:text-green-300 font-medium">Video uploaded successfully</span>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,.mov"
                  onChange={handleVideoUpload}
                  disabled={isUploadingVideo}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/20 dark:file:text-primary-400"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  MP4, WebM, or MOV up to 500MB
                </p>
                {isUploadingVideo && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Uploading: {uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Thumbnail */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Thumbnail Image {!formData.videoUrl && <span className="text-xs text-gray-500">(Upload video first to enable auto-generation)</span>}
            </label>
            <ThumbnailUploader
              onThumbnailChange={handleThumbnailChange}
              videoFile={videoFile}
              videoUrl={formData.videoUrl}
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={isSubmitting || isUploadingVideo || !formData.videoUrl}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Create Website
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/app/private/manageWebsites')}
              className="px-6 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
}
